import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Info, Plus, Save, Shirt, Sparkles, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { inventoryService } from '@/services/inventory';
import type { FabricComponent, InventoryItem } from '@/types/inventory';

// Common apparel materials — operators can also type any value.
const FABRIC_MATERIALS = [
  'Cotton', 'Polyester', 'Viscose', 'Rayon', 'Linen', 'Wool', 'Silk',
  'Elastane', 'Spandex', 'Nylon', 'Modal', 'Lyocell', 'Acrylic',
  'Cashmere', 'Denim', 'Hemp', 'Jute',
];

// Common wash-care instructions offered as one-click quick-adds. The
// operator can edit any added line or type a fully custom one — care
// copy is tenant-specific, so this is only a starting point.
const COMMON_CARE_INSTRUCTIONS = [
  'Machine wash cold',
  'Machine wash on gentle cycle at 30°C',
  'Hand wash',
  'Wash dark colours separately with mild detergent',
  'Wash and dry inside out',
  'Do not bleach',
  'Do not tumble dry',
  'Tumble dry low',
  'Line dry',
  'Dry in shade',
  'Dry flat',
  'Iron at low temperature',
  'Iron at medium temperature',
  'Do not iron',
  'Dry clean if needed',
  'Do not dry clean',
];

// Back-compat — items saved before wash care became free-text stored
// stable codes. Map any such code to a readable line on load.
const LEGACY_CARE_LABEL: Record<string, string> = {
  machine_wash_cold: 'Machine wash cold',
  machine_wash_warm: 'Machine wash warm',
  hand_wash: 'Hand wash',
  gentle_cycle: 'Machine wash on gentle cycle at 30°C',
  wash_separately: 'Wash dark colours separately with mild detergent',
  wash_inside_out: 'Wash and dry inside out',
  do_not_wash: 'Do not wash',
  do_not_bleach: 'Do not bleach',
  non_chlorine_bleach: 'Non-chlorine bleach only',
  tumble_dry_low: 'Tumble dry low',
  tumble_dry_medium: 'Tumble dry low',
  do_not_tumble_dry: 'Do not tumble dry',
  line_dry: 'Line dry',
  flat_dry: 'Dry flat',
  dry_in_shade: 'Dry in shade',
  iron_low: 'Iron at low temperature',
  iron_medium: 'Iron at medium temperature',
  iron_high: 'Iron at medium temperature',
  do_not_iron: 'Do not iron',
  steam_only: 'Iron at low temperature',
  dry_clean: 'Dry clean if needed',
  do_not_dry_clean: 'Do not dry clean',
};

/** Map any stored wash-care values (legacy codes or free text) to readable
 *  lines. Used when hydrating a saved item into the editor. */
export function normaliseCareInstructions(stored: string[] | undefined | null): string[] {
  return (stored ?? []).map((v) => LEGACY_CARE_LABEL[v] ?? v);
}

/** Trim + drop empty fabric/care rows for persistence. Shared by every
 *  consumer so create and edit serialise identically. */
export function normaliseFabricCareValue(value: FabricCareValue): {
  fabricComposition: FabricComponent[];
  washCareInstructions: string[];
  careNotes: string | undefined;
} {
  return {
    fabricComposition: value.fabric
      .filter((c) => c.material.trim())
      .map((c) => ({ material: c.material.trim(), percent: Number(c.percent) || 0 })),
    washCareInstructions: value.careInstructions.map((s) => s.trim()).filter(Boolean),
    careNotes: value.careNotes.trim() || undefined,
  };
}

/** Controlled value carried by {@link FabricCareFields}. */
export interface FabricCareValue {
  fabric: FabricComponent[];
  careInstructions: string[];
  careNotes: string;
}

/**
 * Controlled fabric-composition + wash/care editor — pure value/onChange,
 * no Save button and no data fetching. This lets the same UI serve both the
 * item DETAIL sheet (wrapped by {@link ItemFabricCareTab}, which owns the
 * save mutation) and the item CREATE form (bound straight to the create
 * payload, saved with the item). See ADR-040 S3.
 */
export function FabricCareFields({
  value,
  onChange,
}: {
  value: FabricCareValue;
  onChange: (next: FabricCareValue) => void;
}) {
  const { fabric, careInstructions, careNotes } = value;
  const patch = (p: Partial<FabricCareValue>) => onChange({ ...value, ...p });

  const fabricTotal = useMemo(
    () => fabric.reduce((s, c) => s + (Number(c.percent) || 0), 0),
    [fabric],
  );
  const fabricPreview = useMemo(
    () =>
      fabric
        .filter((c) => c.material.trim())
        .map((c) => `${Number(c.percent) || 0}% ${c.material.trim()}`)
        .join(' · '),
    [fabric],
  );

  // ── Fabric handlers ────────────────────────────────────────────
  const setFabricRow = (i: number, rowPatch: Partial<FabricComponent>) =>
    patch({ fabric: fabric.map((r, idx) => (idx === i ? { ...r, ...rowPatch } : r)) });
  const addFabricRow = () => patch({ fabric: [...fabric, { material: '', percent: 0 }] });
  const removeFabricRow = (i: number) =>
    patch({ fabric: fabric.filter((_, idx) => idx !== i) });

  // ── Care handlers ──────────────────────────────────────────────
  const setCareRow = (i: number, v: string) =>
    patch({ careInstructions: careInstructions.map((r, idx) => (idx === i ? v : r)) });
  const addCareRow = (v = '') => patch({ careInstructions: [...careInstructions, v] });
  const removeCareRow = (i: number) =>
    patch({ careInstructions: careInstructions.filter((_, idx) => idx !== i) });

  return (
    <div className="space-y-4">
      {/* Usage guide */}
      <div className="flex items-start gap-2 rounded-md border border-info/25 bg-info/[0.05] px-3 py-2.5 text-[12px]">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
        <div className="space-y-1 text-muted-foreground">
          <p className="font-medium text-foreground">How to fill this in</p>
          <p>
            <strong>1. Fabric composition</strong> — add each material with its
            percentage; the total should come to 100%.
          </p>
          <p>
            <strong>2. Wash &amp; care</strong> — add one care instruction per
            line. Use <strong>Quick add</strong> for common ones, or type your
            own. They show on the product page in the order listed.
          </p>
        </div>
      </div>

      {/* ── Section 1 — Fabric composition ──────────────────────── */}
      <section className="space-y-3 rounded-lg border border-border/70 bg-card p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shirt className="h-4 w-4 text-muted-foreground" />
            <div>
              <div className="text-[13px] font-semibold text-foreground">
                1 · Fabric composition
              </div>
              <div className="text-[11px] text-muted-foreground">
                e.g. 62% Cotton · 38% Viscose — should total 100%
              </div>
            </div>
          </div>
          {fabric.length > 0 && (
            <Badge
              variant={fabricTotal === 100 ? 'success' : 'warning'}
              className="shrink-0 text-[10px]"
              title="Percentages should total 100%"
            >
              total {fabricTotal}%
            </Badge>
          )}
        </div>

        <datalist id="fabric-materials">
          {FABRIC_MATERIALS.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>

        {fabric.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">
            No materials added yet — click “Add material” to start.
          </p>
        ) : (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 px-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              <span className="max-w-xs flex-1">Material</span>
              <span className="w-24 text-center">Percentage</span>
              <span className="w-7" />
            </div>
            {fabric.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  list="fabric-materials"
                  value={c.material}
                  onChange={(e) => setFabricRow(i, { material: e.target.value })}
                  placeholder="e.g. Cotton"
                  className="h-8 max-w-xs flex-1 text-[12.5px]"
                />
                <div className="flex w-24 items-center">
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={c.percent || ''}
                    onChange={(e) =>
                      setFabricRow(i, { percent: Number(e.target.value) || 0 })
                    }
                    className="h-8 rounded-r-none text-center font-mono text-[12.5px]"
                  />
                  <span className="inline-flex h-8 items-center rounded-r-md border border-l-0 border-input bg-muted/40 px-2 text-[12px] text-muted-foreground">
                    %
                  </span>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeFabricRow(i)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {fabricPreview && (
          <p className="text-[11.5px] text-muted-foreground">
            Shows on the product page as:{' '}
            <span className="font-medium text-foreground">{fabricPreview}</span>
          </p>
        )}
        <Button size="sm" variant="outline" onClick={addFabricRow}>
          <Plus className="h-3.5 w-3.5" />
          Add material
        </Button>
      </section>

      {/* ── Section 2 — Wash & care ─────────────────────────────── */}
      <section className="space-y-3 rounded-lg border border-border/70 bg-card p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="text-[13px] font-semibold text-foreground">
              2 · Wash &amp; care instructions
            </div>
            <div className="text-[11px] text-muted-foreground">
              One instruction per line — add common ones below or type your own
            </div>
          </div>
        </div>

        {/* Instruction list */}
        {careInstructions.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/70 px-3 py-4 text-center text-[12px] text-muted-foreground">
            No care instructions yet. Pick from <strong>Quick add</strong> below,
            or click “Add instruction” to type your own.
          </p>
        ) : (
          <div className="space-y-1.5">
            {careInstructions.map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 text-right text-[12px] tabular-nums text-muted-foreground">
                  {i + 1}.
                </span>
                <Input
                  value={line}
                  onChange={(e) => setCareRow(i, e.target.value)}
                  placeholder="e.g. Machine wash cold"
                  className="h-8 flex-1 text-[12.5px]"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeCareRow(i)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button size="sm" variant="outline" onClick={() => addCareRow()}>
          <Plus className="h-3.5 w-3.5" />
          Add instruction
        </Button>

        {/* Quick add */}
        <div className="space-y-1.5 border-t border-border/50 pt-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            Quick add — common instructions
          </div>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_CARE_INSTRUCTIONS.filter(
              (c) => !careInstructions.includes(c),
            ).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => addCareRow(c)}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/50 hover:text-foreground"
              >
                <Plus className="h-3 w-3" />
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Care notes */}
        <div className="space-y-1.5 border-t border-border/50 pt-3">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            Care notes (optional)
          </div>
          <Textarea
            value={careNotes}
            onChange={(e) => patch({ careNotes: e.target.value })}
            rows={2}
            placeholder="Any extra guidance — e.g. reshape while damp, avoid direct sunlight."
          />
        </div>
      </section>
    </div>
  );
}

/**
 * Fabric & Care tab (item DETAIL sheet). Owns the local draft state + Save
 * mutation and delegates the actual fields to {@link FabricCareFields}.
 * Always editable; saved via the item update endpoint. Feeds the customer
 * portal via getItemBySku.
 */
export function ItemFabricCareTab({ item }: { item: InventoryItem }) {
  const qc = useQueryClient();

  const [fabric, setFabric] = useState<FabricComponent[]>([]);
  const [careInstructions, setCareInstructions] = useState<string[]>([]);
  const [careNotes, setCareNotes] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setFabric(item.fabricComposition ? item.fabricComposition.map((c) => ({ ...c })) : []);
    setCareInstructions(normaliseCareInstructions(item.washCareInstructions));
    setCareNotes(item.careNotes ?? '');
    setDirty(false);
  }, [item]);

  const save = useMutation({
    mutationFn: () =>
      inventoryService.update(
        item.id,
        normaliseFabricCareValue({ fabric, careInstructions, careNotes }),
      ),
    onSuccess: () => {
      toast.success('Fabric & Care saved');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save'),
  });

  return (
    <div className="space-y-4">
      {/* Header — title + Save */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[14px] font-semibold text-foreground">Fabric &amp; Care</h3>
        <Button size="sm" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          <Save className="h-3.5 w-3.5" />
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <FabricCareFields
        value={{ fabric, careInstructions, careNotes }}
        onChange={(v) => {
          setFabric(v.fabric);
          setCareInstructions(v.careInstructions);
          setCareNotes(v.careNotes);
          setDirty(true);
        }}
      />
    </div>
  );
}
